/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import {
	Breadcrumb,
	BreadcrumbPath,
	Button,
	LoaderScreen,
	Typography,
} from '@/components/common';
import { GenericCard } from '@/components/dashboard';
import AppRoutes from '@/const/routes';
import { Tab } from '@headlessui/react';
import { useRouter } from 'next/router';
import Icons from '@/const/icons';
import { AvatarName } from '../../../dashboard/inbox/AvatarName';
import { SegmentClient } from '../common';
import { useQuery } from '@tanstack/react-query';
import {
	GET_CLIENT_ACCOUNT_DETAILS,
	GET_CLIENT_PROFILE_DASHBOARD,
} from '@/api';
import { clientProfileService } from '@/api/clientProfile';
import Link from 'next/link';
import FlagClient from './account-details/FlagClient';
import { NewEmail } from '../inbox/NewEmail';
import { AccountDetailsResponse } from '@/interfaces/clientProfile';
import dynamic from 'next/dynamic';
import { ReviewBanner } from './account-details/ReviewBanner';
import { useLayout } from '@/context/LayoutContext';
import { clientManagementOptions } from '@/const/sideBarNavigation';

const initialPaths = [
	{
		icon: Icons.book,
		href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY,
		name: 'Client Directory',
		current: false,
	},
	{
		icon: Icons.profile,
		href: '',
		name: 'Client Profile',
		current: true,
	},
];

const DashboardScreen = dynamic(
	() => import('./Dashboard').then((mod) => mod.DashboardScreen),
	{ ssr: false }
);
const AccountDetailsScreen = dynamic(
	() =>
		import('./account-details/AccountDetails').then(
			(mod) => mod.AccountDetailsScreen
		),
	{ ssr: false }
);
const CasesScreen = dynamic(
	() => import('./cases/Cases').then((mod) => mod.CasesScreen),
	{ ssr: false }
);
const BrandsUsedScreen = dynamic(
	() => import('./BrandsUsed').then((mod) => mod.BrandsUsedScreen),
	{ ssr: false }
);

export const ClientProfileContentScreen: React.FC = () => {
	const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
	const [, setCaseSelected] = React.useState<string | undefined>();
	const [paths, setPaths] = React.useState<BreadcrumbPath[]>([]);
	const [openNewEmail, setOpenNewMessage] = React.useState<boolean>(false);
	const [isLoad, setIsLoad] = React.useState(false);

	const router = useRouter();
	const pathname = usePathname();
	const { id, tab, applicationId, applicationName, from, backCaseId } =
		router.query;

	const { data: summary, isLoading } = useQuery({
		queryKey: [GET_CLIENT_PROFILE_DASHBOARD],
		queryFn: () => clientProfileService.getDashboard(id as string),
	});

	const {
		data: accountDetails,
		isLoading: isLoadingAccountDetails,
		refetch: refetchAccountDetails,
	} = useQuery({
		queryKey: [GET_CLIENT_ACCOUNT_DETAILS, id],
		queryFn: () => clientProfileService.getAccountDetails(id as string),
		enabled: !!id,
	});

	const { setSubSidebar } = useLayout();

	React.useEffect(() => {
		setSubSidebar({
			title: 'Client Management',
			sections: clientManagementOptions.map((opt) => ({
				...opt,
				className: clsx(opt.className, {
					'bg-bg-primary': opt.name === 'client-directory',
				}),
			})),
		});

		return () => setSubSidebar(null);
	}, []);

	React.useEffect(() => {
		if (accountDetails) {
			setIsLoad(true);
		}
	}, [accountDetails]);

	React.useEffect(() => {
		if (selectedIndex == 2) {
			let crumb = [
				{
					icon: Icons.book,
					href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY,
					name: 'Client Directory',
					current: false,
				},
				{
					icon: Icons.profile,
					href: `${pathname}?tab=dashboard`,
					name: 'Client Profile',
					current: false,
				},
				{
					icon: Icons.briefcase,
					href: `${pathname}?tab=cases`,
					name: 'Client Cases',
					current: true,
				},
			];

			if (from) {
				const auxCrumb = [...crumb];
				auxCrumb.splice(1, 0, {
					icon: Icons.flag,
					href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY_CLEANUP_WIZARD,
					name: 'Client Cleanup Wizard',
					current: false,
				});
				crumb = auxCrumb;
			}

			if (applicationId) {
				crumb = [
					{
						icon: Icons.book,
						href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY,
						name: 'Client Directory',
						current: false,
					},
					{
						icon: Icons.profile,
						href: `${pathname}?tab=dashboard`,
						name: 'Client Profile',
						current: false,
					},
					{
						icon: Icons.briefcase,
						href: `${pathname}?tab=cases`,
						name: 'Client Cases',
						current: false,
					},
					{
						icon: Icons.document,
						href: `${pathname}?applicationId=${applicationId}&applicationName=${applicationName}&tab=cases`,
						name: applicationName as string,
						current: true,
					},
				];
				if (from) {
					const auxCrumb = [...crumb];
					auxCrumb.splice(1, 0, {
						icon: Icons.flag,
						href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY_CLEANUP_WIZARD,
						name: 'Client Cleanup Wizard',
						current: false,
					});
					crumb = auxCrumb;
				}
			} else {
				setCaseSelected(undefined);
			}
			setPaths([...crumb]);
		} else {
			const auxInitialPaths = [...initialPaths];
			if (from) {
				auxInitialPaths.splice(1, 0, {
					icon: Icons.flag,
					href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY_CLEANUP_WIZARD,
					name: 'Client Cleanup Wizard',
					current: false,
				});
			}
			setPaths(auxInitialPaths);
		}
	}, [selectedIndex, applicationId]);

	const commonOptions = [
		{
			label: 'Dashboard',
			name: 'dashboard',
			href: `${pathname}?tab=dashboard${
				backCaseId ? `&backCaseId=${backCaseId}` : ''
			}`,
		},
		{
			label: 'Account Details',
			name: 'account-details',
			href: `${pathname}?tab=account-details${
				backCaseId ? `&backCaseId=${backCaseId}` : ''
			}`,
		},
		{
			label: 'Cases',
			name: 'cases',
			href: `${pathname}?tab=cases${
				backCaseId ? `&backCaseId=${backCaseId}` : ''
			}`,
		},
		{
			label: 'Brands Used',
			name: 'brands-used',
			href: `${pathname}?tab=brands-used${
				backCaseId ? `&backCaseId=${backCaseId}` : ''
			}`,
		},
	];

	React.useEffect(() => {
		if (!tab) return;
		const tabIndex = commonOptions.findIndex((option) => option.name === tab);
		setSelectedIndex(tabIndex !== -1 ? tabIndex : 0);
	}, [tab]);

	const isActive = accountDetails && !accountDetails?.data.deleted_at;
	const showReviewBanner =
		accountDetails && accountDetails?.data.isFlagged?.result;
	const segmentationInactiveList = [
		'Deactivated Accounts',
		'Deleted Accounts',
		'Unverified Accounts',
		'Flagged Customer',
	];
	const isSegmentationInactive = segmentationInactiveList.includes(
		accountDetails?.data.segmentation ?? ''
	);
	// const hasClientName =
	// 	!!accountDetails?.data.firstname || !!accountDetails?.data.lastname;

	if (isLoading) return <LoaderScreen />;

	const getAccountName = (
		accountDetails: AccountDetailsResponse | undefined
	): string => {
		if (accountDetails?.firstname) {
			return accountDetails?.firstname;
		}

		return 'Unnamed';
	};
	return (
		<>
			<div className="">
				<Breadcrumb
					paths={paths}
					keepThisParam={from ? `from=${from}` : undefined}
				/>
			</div>
			<GenericCard className="mt-8 md:min-h-[50vh] 2xl:min-h-[80vh]">
				{!isLoadingAccountDetails && showReviewBanner && isActive && (
					<ReviewBanner />
				)}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
					<div className="flex items-center gap-4 sm:gap-6">
						<AvatarName
							isBold
							photoUrl={
								accountDetails?.data.firstname
									? accountDetails?.data.profile_picture
									: Icons.avatar
							}
							firstname={getAccountName(accountDetails?.data)}
							lastname={accountDetails?.data.lastname || ''}
							size="small"
							classNameText="!text-lg capitalize"
							classNameAvatar="!h-10 !w-10"
						/>
						{accountDetails?.data.segmentation && (
							<SegmentClient
								label={accountDetails?.data.segmentation}
								withTextColor
								className="!max-w-fit"
							/>
						)}
					</div>
					<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
						{!showReviewBanner &&
							!isSegmentationInactive &&
							isActive &&
							isLoad && (
								<div className="w-full sm:w-auto">
									<FlagClient
										align="end"
										side="bottom"
										clientId={id as string}
										refetchData={refetchAccountDetails}
									/>
								</div>
							)}

						{/* {hasClientName && (
							<Button
								decoration="secondary"
								size="medium"
								className={clsx('flex items-center gap-2 w-full sm:w-auto', {
									hidden: !isActive,
								})}
								onClick={() => {
									const query = {
										...router.query,
										clientId: id,
									};

									router.push({
										pathname: router.pathname,
										query: query,
									});

									setOpenNewMessage(true);
								}}
							>
								<Icon src={Icons.edit} className="!w-4 !h-4" />
								<Typography type="semibold-16" className="hidden sm:block">
									Message Client
								</Typography>
								<Typography type="semibold-16" className="sm:hidden">
									Message
								</Typography>
							</Button>
						)} */}

						{backCaseId && (
							<Button
								label="Back"
								size="fit"
								icon={Icons.arrowLeft}
								iconLeft
								className="border-none w-full sm:w-auto"
								onClick={() =>
									router.push(
										`${AppRoutes.LAWYERAPP_CASES}/${backCaseId}?tab=case-clients&userId=${id}`
									)
								}
							/>
						)}
					</div>
				</div>
				<Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
					<div className="w-full min-h-[50px] overflow-x-auto mt-6 px-4 sm:px-0">
						<Tab.List
							className={
								'flex items-center gap-3 sm:gap-5 border-b pb-0 w-max min-w-full'
							}
						>
							{commonOptions.map((item, index) => (
								<Link
									key={`${index}`}
									href={from ? `${item.href}&from=${from}` : item?.href || ''}
									passHref
								>
									<Tab
										key={index}
										className={clsx('pb-4 whitespace-nowrap', {
											'border-b-2 border-purple-150 outline-none ':
												selectedIndex === index,
										})}
									>
										<Typography
											type="semibold-14"
											className={clsx('text-xs sm:text-sm', {
												'text-purple-150': index === selectedIndex,
												'opacity-60': index !== selectedIndex,
											})}
										>
											{item.label}
										</Typography>
									</Tab>
								</Link>
							))}
						</Tab.List>
					</div>
					<Tab.Panels className={'mt-8 px-4 sm:px-0'}>
						<Tab.Panel
							className={'flex flex-col mb-8 2xl:gap-8 justify-around h-full'}
						>
							<DashboardScreen summary={summary?.data} />
						</Tab.Panel>
						<Tab.Panel>
							<AccountDetailsScreen
								accountData={accountDetails?.data}
								isLoading={isLoadingAccountDetails}
							/>
						</Tab.Panel>
						<Tab.Panel>
							<CasesScreen setCaseSelected={setCaseSelected} />
						</Tab.Panel>
						<Tab.Panel className={'min-h-[560px]'}>
							<BrandsUsedScreen />
						</Tab.Panel>
					</Tab.Panels>
				</Tab.Group>
			</GenericCard>

			{/* Message Client */}
			<NewEmail
				openNewEmail={openNewEmail}
				setOpenNewMessage={setOpenNewMessage}
			/>
		</>
	);
};
